import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Grid,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Stepper,
  Text,
  TextInput,
  Title,
  TypographyStylesProvider,
} from "@mantine/core";
import DOMPurify from "dompurify";
import { getApiEndpoint, getCompanyApiEndpoint } from "../../config/config";
import "./InfoVerificationPage.css";

// How far FitText may scale content UP. Content wider than its container is
// always shrunk to fit; short content (a lone plan name) stops here instead of
// stretching edge to edge, which would tower over the two-plan header.
const MAX_FIT_SCALE = 1.25;

// Scales its single-line content toward the container width, remeasuring on
// resize. Used for the plan headers so a long two-plan row stays on one line.
const FitText = ({ children, className }) => {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [fit, setFit] = useState({ ratio: 1, height: null });

  // Layout effect, not a plain effect: measuring after paint would show the
  // content at its unscaled size for a frame, which reads as a jump every time
  // a step mounts. This runs before the browser paints, so the first frame is
  // already at the right scale.
  useLayoutEffect(() => {
    const measure = () => {
      const outer = outerRef.current;
      const inner = innerRef.current;
      if (!outer || !inner) return;
      // offsetWidth is the pre-transform layout width, so this stays stable
      // across re-measures rather than compounding the previous ratio.
      const naturalWidth = inner.offsetWidth;
      const naturalHeight = inner.offsetHeight;
      if (naturalWidth > 0) {
        // Shrink to fit, but only stretch so far: a single short plan name
        // blown up to the full card width dwarfs the two-plan header, so cap
        // the scale and let short content sit centred at a modest size.
        const ratio = Math.min(MAX_FIT_SCALE, outer.clientWidth / naturalWidth);
        setFit({ ratio, height: naturalHeight * ratio });
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (outerRef.current) observer.observe(outerRef.current);
    return () => observer.disconnect();
  }, [children]);

  return (
    <div
      ref={outerRef}
      style={{
        width: "100%",
        height: fit.height ?? undefined,
        // Content narrower than the container is centred; content that had to
        // be scaled down already spans the full width, and centring it would
        // push it off the left edge.
        textAlign: fit.ratio < MAX_FIT_SCALE ? "left" : "center",
      }}
    >
      <div
        ref={innerRef}
        className={className}
        style={{
          display: "inline-flex",
          alignItems: "baseline",
          whiteSpace: "nowrap",
          transform: `scale(${fit.ratio})`,
          transformOrigin:
            fit.ratio < MAX_FIT_SCALE ? "left top" : "center top",
        }}
      >
        {children}
      </div>
    </div>
  );
};

// Display a raw phone value as (555) 555-5555. Handles a leading "1" country
// code; anything that isn't a 10-digit US number is shown as-is.
function formatPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  const local =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length !== 10) return value || "";
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}

// Pest entries arrive as { name, image_url } objects (picked from the
// FieldRoutes insect catalog); older payloads were plain strings. Normalize to
// the object shape either way.
function normalizePests(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map(pest =>
      typeof pest === "string"
        ? { name: pest, image_url: null }
        : { name: pest?.name || "", image_url: pest?.image_url || null }
    )
    .filter(pest => pest.name);
}

// Parse a money value that may arrive as a number (840) or a string ("$840.00"
// / "840"). Null when there's nothing numeric to read.
function parseMoney(value) {
  if (value == null || value === "") return null;
  const amount =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(amount) ? amount : null;
}

// Format a parsed amount as $1,234.56.
function formatMoney(amount) {
  return amount == null
    ? ""
    : amount.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
}

// Fill authoring tokens in the contract HTML with live plan values. Right now
// that's just {{CANCELLATION_FEE}} (case-insensitive, tolerant of inner spaces),
// taken from the first plan that carries a fee. Real surveys may already have
// this substituted server-side, in which case there's no token left and this is
// a no-op; the live preview builds its payload client-side, so it relies on this.
function fillContractTokens(html, subscriptions = []) {
  const fee = subscriptions.map(s => s.cancellationFee).find(v => v != null);
  return html.replace(
    /\{\{\s*CANCELLATION_FEE\s*\}\}/gi,
    fee == null ? "" : formatMoney(fee)
  );
}

// Merge pest lists from several plans into one, keeping the first entry for a
// given name (so a plan with a photo wins over one without).
function dedupePests(pests) {
  const byName = new Map();
  pests.forEach(pest => {
    const existing = byName.get(pest.name);
    if (!existing) byName.set(pest.name, pest);
    else if (!existing.image_url && pest.image_url) byName.set(pest.name, pest);
  });
  return [...byName.values()];
}

// A stable per-device identifier, generated once and persisted in localStorage.
function getDeviceId() {
  try {
    let id = localStorage.getItem("device_id");
    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem("device_id", id);
    }
    return id;
  } catch {
    return "";
  }
}

// Map a single subscription/plan onto the shape the Subscription and Schedule
// steps render. Supports both snake_case and camelCase keys.
function mapSubscription(subscription) {
  const s = subscription || {};
  return {
    id: s.fr_subscription_id ?? s.id ?? null,
    serviceType: s.service_type || s.serviceType || "",
    agreementLength: s.agreement_length || s.agreementLength || "",
    initialPayment:
      s.initial_amount || s.initial_service_payment || s.initialPayment || "",
    recurringPayment:
      s.recurring_amount || s.recurring_payment || s.recurringPayment || "",
    // Customer-facing billing cadence ("Billed monthly"), so the recurring
    // amount isn't shown without saying how often it's charged.
    billingFrequency:
      s.billing_frequency_label || s.billingFrequencyLabel || "",
    // FieldRoutes seasonal window (months 1-12); null on year-round services.
    seasonStartMonth: s.season_start_month ?? s.seasonStartMonth ?? null,
    seasonEndMonth: s.season_end_month ?? s.seasonEndMonth ?? null,
    estimatedHomeVisits:
      s.estimated_visits ||
      s.estimated_home_visits ||
      s.estimatedHomeVisits ||
      "",
    visitsPerYear: s.visits_per_year ?? s.visitsPerYear ?? null,
    // Fills {{CANCELLATION_FEE}} in the contract terms; null when unset.
    cancellationFee: parseMoney(s.cancellation_fee ?? s.cancellationFee),
    serviceMonths: Array.isArray(s.service_months)
      ? s.service_months
      : Array.isArray(s.serviceMonths)
        ? s.serviceMonths
        : [],
    includedPests: normalizePests(s.included_pests),
    // Older payloads used specialty_pests; both render as "Not included".
    notIncludedPests: normalizePests(s.not_included_pests ?? s.specialty_pests),
    // Per-visit-month schedule entries: { month, season_name, target_pests }.
    serviceSchedule: Array.isArray(s.service_schedule)
      ? s.service_schedule
      : [],
  };
}

// Map the prefill payload returned by the API (by survey code) onto the form.
// Supports both snake_case and camelCase keys. The plan details now arrive as a
// `subscriptions` array; older payloads carried a single plan's fields inline,
// which we wrap into a one-item list for backward compatibility.
function mapPrefill(data) {
  const d = data || {};
  const rawSubscriptions =
    Array.isArray(d.subscriptions) && d.subscriptions.length > 0
      ? d.subscriptions
      : [d];
  return {
    name: d.name || d.full_name || "",
    email: d.email || "",
    address: d.address || d.street || "",
    city: d.city || "",
    state: d.state || "",
    zip: d.zip || d.zip_code || d.postal_code || "",
    phone: d.phone || d.phone_number || "",
    contractText: d.welcome_survey_contract_text || d.contractText || "",
    termsText: d.welcome_survey_terms_text || d.termsText || "",
    headerImageUrl: d.header_image_url || d.headerImageUrl || "",
    accentColor: d.accent_color || d.accentColor || "",
    subscriptions: rawSubscriptions.map(mapSubscription),
  };
}

// Month labels for the service-schedule cards; service_months from the API is
// 1-based (1 = January).
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// `previewData` renders the survey from a passed-in payload instead of fetching
// one by code, and turns submit into a no-op — used by the Survey Setup page's
// live preview so a company can walk its own survey without a real customer or
// a real submission. Live customers never pass it. `previewStep` opens a
// preview on a given step (0-4), so the panel can land on the step being edited.
const InfoVerificationPage = ({ previewData = null, previewStep = 0 }) => {
  // Live surveys are opened at /welcomesurvey/:client/:surveyCode.
  const { client, surveyCode: pathSurveyCode } = useParams();

  const isPreview = previewData != null;
  const surveyCode = pathSurveyCode || "";

  // Customers open this from a text without ever logging in, so there's no
  // stored company to build the API host from — the link leads with the client
  // it belongs to. The admin preview has no client and makes no calls anyway,
  // so it falls back to the stored company.
  const surveyEndpoint = useCallback(
    path =>
      client ? getCompanyApiEndpoint(client, path) : getApiEndpoint(path),
    [client]
  );

  const [active, setActive] = useState(isPreview ? previewStep : 0);
  const [confirmed, setConfirmed] = useState(false);
  const [subscriptionConfirmed, setSubscriptionConfirmed] = useState(false);
  // Whether the combined pest list is expanded past its first row.
  const [pestsExpanded, setPestsExpanded] = useState(false);
  const [contractAccepted, setContractAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const [loading, setLoading] = useState(!isPreview);
  const [loadError, setLoadError] = useState(false);

  const [form, setForm] = useState(() => mapPrefill(previewData));

  const sigCanvasRef = useRef(null);

  const clearSignature = () => sigCanvasRef.current?.clear();

  // Company-chosen accent color (hex) for the stepper, buttons and checkboxes;
  // undefined falls back to the Mantine theme primary. accentValue is the
  // concrete CSS color for inline styles (schedule card borders / labels).
  const accent = form.accentColor || undefined;
  const accentValue = form.accentColor || "var(--mantine-primary-color-filled)";

  // The plan header shared by the Subscription and Schedule steps. Each plan is
  // { key, name, lines: [term, cost] } — the accent name with up to two lines
  // beneath it. One plan stays on a single scaled line; multiple plans stack on
  // top of each other, each scaled to the card on its own, so long names never
  // have to share one row.
  const renderPlanHeader = plans => {
    const planColumn = plan => (
      <span className="subscription-header-plan">
        <span className="subscription-name" style={{ color: accentValue }}>
          {plan.name}
        </span>
        {plan.lines.filter(Boolean).map((line, lineIndex) => (
          <span
            key={lineIndex}
            className={
              lineIndex === 0 ? "subscription-term" : "subscription-cost"
            }
          >
            {line}
          </span>
        ))}
      </span>
    );

    if (plans.length <= 1) {
      return (
        <FitText className="subscription-stats">
          <span className="subscription-header">
            {plans.map(plan => (
              <React.Fragment key={plan.key}>{planColumn(plan)}</React.Fragment>
            ))}
          </span>
        </FitText>
      );
    }

    return (
      <Stack gap="lg" align="center">
        {plans.map(plan => (
          <FitText key={plan.key} className="subscription-stats">
            <span className="subscription-header">{planColumn(plan)}</span>
          </FitText>
        ))}
      </Stack>
    );
  };

  // Fetch the prefill data for this survey code so the person has something to
  // review. Verification still happens server-side on submit.
  useEffect(() => {
    // Preview mode renders the payload it was handed, so there's nothing to
    // fetch — just keep the form in step with it as the admin page reloads it.
    if (isPreview) {
      setForm(mapPrefill(previewData));
      setLoading(false);
      setLoadError(false);
      return undefined;
    }

    if (!surveyCode) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const fetchPrefill = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const response = await fetch(
          surveyEndpoint(
            `/v1/welcome-survey/survey?survey_code=${encodeURIComponent(surveyCode)}`
          )
        );
        if (!response.ok) throw new Error("verify-info-unavailable");
        const data = await response.json();
        if (!cancelled) setForm(mapPrefill(data));
      } catch (err) {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPrefill();
    return () => {
      cancelled = true;
    };
  }, [surveyCode, isPreview, previewData, surveyEndpoint]);

  const nextStep = () =>
    setActive(current => (current < 4 ? current + 1 : current));
  const prevStep = () =>
    setActive(current => (current > 0 ? current - 1 : current));

  const handleSubmit = async () => {
    setError("");

    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      setError("Please add your initials below to confirm.");
      return;
    }

    // A preview walks the whole flow but never posts — there's no survey behind
    // it to complete, and nothing should reach FieldRoutes.
    if (isPreview) {
      setSubmitted(true);
      return;
    }

    const initials = sigCanvasRef.current.toDataURL();

    setSubmitting(true);
    try {
      const response = await fetch(
        surveyEndpoint("/v1/welcome-survey/submit"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            survey_code: surveyCode,
            initials,
            device_id: getDeviceId(),
          }),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          data.message || "Something went wrong. Please try again."
        );
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!surveyCode && !isPreview) {
    return (
      <div className="verify-page">
        <Paper className="verify-card" shadow="sm" radius="md" p="xl">
          <Alert color="red" title="Invalid link">
            This verification link is missing its survey code. Please use the
            link your rep sent you.
          </Alert>
        </Paper>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="verify-page">
        <Paper className="verify-card" shadow="sm" radius="md" p="xl">
          <Stack align="center" gap="sm">
            <Loader />
            <Text c="dimmed">Loading…</Text>
          </Stack>
        </Paper>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="verify-page">
        <Paper className="verify-card" shadow="sm" radius="md" p="xl">
          <Alert color="red" title="Invalid link">
            We couldn't find the welcome survey with this link. Please use the
            link your rep sent you, or reach out to your rep for help.
          </Alert>
        </Paper>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="verify-page">
        <Paper className="verify-card" shadow="sm" radius="md" p="xl">
          <Stack align="center" gap="sm">
            <div className="verify-success-icon">✓</div>
            <Title order={2}>Thank you!</Title>
            <Text c="dimmed" ta="center">
              Your information has been submitted. You can close this page.
            </Text>
          </Stack>
        </Paper>
      </div>
    );
  }

  return (
    <div className="verify-page">
      <Paper className="verify-card" shadow="sm" radius="md" p="xl">
        {/* Company-branded hero banner, when the company has uploaded one */}
        {form.headerImageUrl && (
          <Box className="verify-header-image">
            <img src={form.headerImageUrl} alt="" />
          </Box>
        )}
        <Stepper
          active={active}
          onStepClick={setActive}
          allowNextStepsSelect={false}
          size="xs"
          iconSize={24}
          color={accent}
        >
          {/* ── Step 1: Verify your information ─────────────────────────── */}
          <Stepper.Step label="Your info" description="Verify details">
            <Alert color={accent} radius="md" mt="md" mb="lg">
              Welcome! Please review your treatment information to ensure
              we&apos;ve got you covered. Please let your sales representative
              know if anything looks incorrect.
            </Alert>

            <Grid gutter="md">
              <Grid.Col span={{ base: 12 }}>
                <TextInput
                  label="Name"
                  placeholder="Full name"
                  value={form.name}
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12 }}>
                <TextInput
                  label="Phone"
                  type="tel"
                  placeholder="(555) 555-5555"
                  value={formatPhone(form.phone)}
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12 }}>
                <TextInput
                  label="Email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12 }}>
                <TextInput
                  label="Address"
                  placeholder="Street address"
                  value={form.address}
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput label="City" value={form.city} readOnly />
              </Grid.Col>
              <Grid.Col span={{ base: 6, sm: 3 }}>
                <TextInput label="State" value={form.state} readOnly />
              </Grid.Col>
              <Grid.Col span={{ base: 6, sm: 3 }}>
                <TextInput label="ZIP" value={form.zip} readOnly />
              </Grid.Col>
            </Grid>

            <Checkbox
              color={accent}
              mt="xl"
              checked={confirmed}
              onChange={e => setConfirmed(e.currentTarget.checked)}
              label="I confirm that the information above is correct."
            />

            <Group justify="flex-end" mt="xl">
              <Button color={accent} onClick={nextStep} disabled={!confirmed}>
                Next
              </Button>
            </Group>
          </Stepper.Step>

          {/* ── Step 2: Subscription ───────────────────────────────────── */}
          <Stepper.Step label="Subscription" description="Verify plan">
            {(() => {
              const subs = form.subscriptions;
              const planNames = subs.map(
                (sub, index) => sub.serviceType || `Plan ${index + 1}`
              );
              // The initial charge is one figure for the whole package;
              // recurring stays per plan in the header, since each carries its
              // own billing cadence.
              const amounts = subs.map(sub => parseMoney(sub.initialPayment));
              const initialTotal = amounts.some(amount => amount != null)
                ? amounts.reduce((total, amount) => total + (amount ?? 0), 0)
                : null;
              // Coverage is combined across plans: a pest any plan covers is
              // included, and "Not included" only lists what no plan covers.
              const includedPests = dedupePests(
                subs.flatMap(sub => sub.includedPests)
              );
              const includedNames = new Set(
                includedPests.map(pest => pest.name)
              );
              const notIncludedPests = dedupePests(
                subs.flatMap(sub => sub.notIncludedPests)
              ).filter(pest => !includedNames.has(pest.name));
              // 4-up grid of pest photos; collapse to the first 7 with a
              // "Show more" cell when there are more.
              const collapsible = includedPests.length > 8;
              const visiblePests =
                collapsible && !pestsExpanded
                  ? includedPests.slice(0, 7)
                  : includedPests;

              // Covered pests across every plan, when mapped by the company —
              // shared by the single-service and bundled layouts below.
              const coverage = (includedPests.length > 0 ||
                notIncludedPests.length > 0) && (
                <Box mt="lg">
                  {includedPests.length > 0 && (
                    <Box mt="sm">
                      <Text size="sm" fw={600} c="dimmed">
                        Target Pests
                      </Text>
                      <SimpleGrid cols={4} spacing="xs" mt="xs">
                        {visiblePests.map(pest => (
                          <Box key={pest.name} className="pest-grid-cell">
                            <Avatar
                              src={pest.image_url}
                              alt={pest.name}
                              size={56}
                              radius="xl"
                              className="pest-avatar"
                            >
                              {pest.name.charAt(0)}
                            </Avatar>
                            <Text size="xs" fw={500} ta="center" mt={6}>
                              {pest.name}
                            </Text>
                          </Box>
                        ))}
                        {collapsible && (
                          <Box
                            className="pest-grid-cell pest-more-cell"
                            onClick={() => setPestsExpanded(prev => !prev)}
                          >
                            <Box
                              className="pest-more-circle"
                              style={{ color: accentValue }}
                            >
                              {pestsExpanded ? "▴" : "▾"}
                            </Box>
                            <Text size="xs" fw={500} ta="center" mt={6}>
                              {pestsExpanded ? "Show less" : "Show more"}
                            </Text>
                          </Box>
                        )}
                      </SimpleGrid>
                    </Box>
                  )}
                  {notIncludedPests.length > 0 && (
                    <Box mt="sm">
                      <Text size="sm" fw={600} c="dimmed">
                        Not included
                      </Text>
                      <Group gap="xs" mt="xs">
                        {notIncludedPests.map(pest => (
                          <Badge key={pest.name} variant="light" color="gray">
                            {pest.name}
                          </Badge>
                        ))}
                      </Group>
                    </Box>
                  )}
                </Box>
              );

              // A single service isn't a package, so it reads as review fields
              // in the same style as the "Your info" step rather than as a
              // combined header. Two or more keep the bundled header.
              if (subs.length === 1) {
                const only = subs[0];
                const recurring = parseMoney(only.recurringPayment);
                return (
                  <Box mt="md">
                    <Grid gutter="md">
                      <Grid.Col span={{ base: 12 }}>
                        <TextInput
                          label="Service"
                          value={planNames[0]}
                          readOnly
                        />
                      </Grid.Col>
                      {only.agreementLength ? (
                        <Grid.Col span={{ base: 12 }}>
                          <TextInput
                            label="Agreement"
                            value={`${only.agreementLength} months`}
                            readOnly
                          />
                        </Grid.Col>
                      ) : null}
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <TextInput
                          label="Initial charge"
                          placeholder="$0.00"
                          value={
                            initialTotal != null
                              ? formatMoney(initialTotal)
                              : ""
                          }
                          readOnly
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <TextInput
                          label="Recurring payment"
                          placeholder="$0.00"
                          value={[
                            recurring != null ? formatMoney(recurring) : "",
                            only.billingFrequency,
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          readOnly
                        />
                      </Grid.Col>
                    </Grid>

                    {coverage}
                  </Box>
                );
              }

              return (
                <Box mt="md">
                  {/* Each plan in the company accent with its own agreement
                      length and recurring cost beneath it. Multiple plans stack
                      on top of each other rather than sharing one row. */}
                  {renderPlanHeader(
                    subs.map((sub, index) => {
                      const recurring = parseMoney(sub.recurringPayment);
                      return {
                        key: sub.id ?? index,
                        name: planNames[index],
                        lines: [
                          sub.agreementLength
                            ? `${sub.agreementLength} Month Agreement`
                            : "",
                          recurring != null
                            ? [formatMoney(recurring), sub.billingFrequency]
                                .filter(Boolean)
                                .join(" ")
                            : "",
                        ],
                      };
                    })
                  )}

                  {/* The initial charge is one combined figure for the whole
                      package, so it sits under the plans rather than with any
                      one of them; each plan's own recurring cost is above. */}
                  {initialTotal != null && (
                    <Text className="subscription-charge" ta="center" mt="lg">
                      <span className="subscription-charge-label">
                        Initial charge:
                      </span>{" "}
                      <span className="subscription-charge-value">
                        {formatMoney(initialTotal)}
                      </span>
                    </Text>
                  )}

                  {coverage}
                </Box>
              );
            })()}

            <Checkbox
              color={accent}
              mt="xl"
              checked={subscriptionConfirmed}
              onChange={e => setSubscriptionConfirmed(e.currentTarget.checked)}
              label="I confirm that the subscription information above is correct."
            />

            <Group justify="space-between" mt="xl">
              <Button variant="default" onClick={prevStep}>
                Back
              </Button>
              <Button
                color={accent}
                onClick={nextStep}
                disabled={!subscriptionConfirmed}
              >
                Next
              </Button>
            </Group>
          </Stepper.Step>

          {/* ── Step 3: Schedule ───────────────────────────────────────── */}
          <Stepper.Step label="Schedule" description="Review & accept">
            {(() => {
              const subs = form.subscriptions;
              const scheduled = subs.filter(
                sub =>
                  sub.visitsPerYear != null ||
                  sub.estimatedHomeVisits !== "" ||
                  sub.serviceMonths.length > 0
              );
              if (scheduled.length === 0) return null;

              // Every plan shares one calendar: a month is a service month if
              // any plan visits it, and a month both plans visit merges their
              // target pests and season names into the single card.
              const sumField = pick => {
                const values = scheduled
                  .map(pick)
                  .map(value =>
                    value === "" || value == null ? null : Number(value)
                  )
                  .filter(value => value != null && Number.isFinite(value));
                return values.length > 0
                  ? values.reduce((total, value) => total + value, 0)
                  : null;
              };

              // Visits a year counts what the calendar below actually shows: a
              // month two plans both visit is one highlighted month, so summing
              // each plan's visits would overcount it. Falls back to the summed
              // per-plan figures when no plan reports service months.
              const mergedMonths = [
                ...new Set(scheduled.flatMap(sub => sub.serviceMonths)),
              ];
              const totalVisits =
                mergedMonths.length > 0
                  ? mergedMonths.length
                  : sumField(sub => sub.visitsPerYear);
              // The calendar is a one-year snapshot, so treatments is the
              // yearly count too — each plan's annualRecurringServices from
              // FieldRoutes, summed. Months a plan shares with another hold
              // more than one treatment, which is why this runs ahead of the
              // lit-month count rather than matching it.
              const totalTreatments = sumField(sub => sub.visitsPerYear);

              // The merged grid can't say which plan covers which month, so
              // each plan gets a line naming the window it runs in — its
              // seasonal range, or year round when FieldRoutes has no season.
              const monthsSummary = sub =>
                sub.seasonStartMonth != null && sub.seasonEndMonth != null
                  ? `${MONTHS[sub.seasonStartMonth - 1]} – ${
                      MONTHS[sub.seasonEndMonth - 1]
                    }`
                  : "Year Round";
              const planWindows = scheduled.map((sub, subIndex) => ({
                key: sub.id ?? subIndex,
                name: sub.serviceType || `Plan ${subIndex + 1}`,
                window: monthsSummary(sub),
                // Only worth breaking out per plan when there's more than one:
                // on a single service it would just restate the total below.
                treatments:
                  scheduled.length > 1 && sub.visitsPerYear != null
                    ? `${sub.visitsPerYear} treatments`
                    : "",
              }));

              const anyMonths = scheduled.some(
                sub => sub.serviceMonths.length > 0
              );

              return (
                <Box mt="md">
                  {/* Each plan and the window it runs in, ahead of the totals
                      and the calendar they apply to. Same header as the
                      Subscription step, so the two render alike: multiple plans
                      stack on top of each other, each name over its own
                      window. */}
                  <Box mb="lg">
                    {renderPlanHeader(
                      planWindows.map(plan => ({
                        key: plan.key,
                        name: plan.name,
                        lines: [plan.window, plan.treatments],
                      }))
                    )}
                  </Box>
                  {(totalVisits != null || totalTreatments != null) && (
                    <Text className="schedule-stat" ta="center" mt="lg" mb="lg">
                      {totalVisits != null && (
                        <>
                          <span
                            className="schedule-stat-number"
                            style={{ color: accentValue }}
                          >
                            {totalVisits}
                          </span>{" "}
                          visits a year
                        </>
                      )}
                      {totalVisits != null &&
                        totalTreatments != null &&
                        " for "}
                      {totalTreatments != null && (
                        <>
                          <span
                            className="schedule-stat-number"
                            style={{ color: accentValue }}
                          >
                            {totalTreatments}
                          </span>{" "}
                          total treatments
                        </>
                      )}
                    </Text>
                  )}
                  {anyMonths && (
                    <SimpleGrid cols={{ base: 2, xs: 3 }} spacing="xs" mt="md">
                      {MONTHS.map((month, index) => {
                        const monthNumber = index + 1;
                        const visiting = scheduled.filter(sub =>
                          sub.serviceMonths.includes(monthNumber)
                        );

                        if (visiting.length === 0) {
                          return (
                            <Box key={month} className="schedule-month-card">
                              <Text size="sm" fw={700}>
                                {month}
                              </Text>
                              <Text
                                size="xs"
                                c="dimmed"
                                className="schedule-month-footer"
                              >
                                Free Call-In
                              </Text>
                            </Box>
                          );
                        }

                        const entries = visiting.map(sub =>
                          sub.serviceSchedule.find(
                            item => item.month === monthNumber
                          )
                        );
                        const targetPests = dedupePests(
                          entries.flatMap(entry =>
                            normalizePests(entry?.target_pests)
                          )
                        );
                        const seasonNames = [
                          ...new Set(
                            entries
                              .map(entry => entry?.season_name)
                              .filter(Boolean)
                          ),
                        ];

                        return (
                          <Box
                            key={month}
                            className="schedule-month-card active"
                            style={{ borderColor: accentValue }}
                          >
                            <Text size="sm" fw={700}>
                              {month}
                            </Text>
                            {targetPests.length > 0 && (
                              <>
                                <Text
                                  size="9px"
                                  c="dimmed"
                                  tt="uppercase"
                                  mt={4}
                                >
                                  Target pests
                                </Text>
                                <Group
                                  gap={4}
                                  justify="center"
                                  mt={4}
                                  wrap="wrap"
                                >
                                  {targetPests.map(pest => (
                                    <Avatar
                                      key={pest.name}
                                      src={pest.image_url}
                                      alt={pest.name}
                                      title={pest.name}
                                      size={28}
                                      radius="xl"
                                    >
                                      {pest.name.charAt(0)}
                                    </Avatar>
                                  ))}
                                </Group>
                              </>
                            )}
                            <Text
                              size="xs"
                              fw={600}
                              className="schedule-month-footer"
                              style={{ color: accentValue }}
                            >
                              {seasonNames.length > 0
                                ? seasonNames.join(" + ")
                                : "Service Visit"}
                            </Text>
                          </Box>
                        );
                      })}
                    </SimpleGrid>
                  )}
                  {/* Sits midway between the calendar above and the
                      confirmation below. */}
                  <Text size="sm" mt={22} style={{ color: accentValue }}>
                    Backed by our pest-free guarantee with free reservices
                    anytime as needed
                  </Text>
                </Box>
              );
            })()}

            <Checkbox
              color={accent}
              mt={22}
              checked={contractAccepted}
              onChange={e => setContractAccepted(e.currentTarget.checked)}
              label="I understand the coverage and schedule above."
            />

            <Group justify="space-between" mt="xl">
              <Button variant="default" onClick={prevStep}>
                Back
              </Button>
              <Button
                color={accent}
                onClick={nextStep}
                disabled={!contractAccepted}
              >
                Next
              </Button>
            </Group>
          </Stepper.Step>

          {/* ── Step 4: Terms ──────────────────────────────────────────── */}
          <Stepper.Step label="Terms" description="Review & accept">
            <Box className="agreement-text" mt="md">
              {form.contractText ? (
                // Contract terms are authored as HTML in the welcome survey
                // settings editor; sanitize since this is public-facing.
                <TypographyStylesProvider>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        fillContractTokens(
                          form.contractText,
                          form.subscriptions
                        )
                      ),
                    }}
                  />
                </TypographyStylesProvider>
              ) : (
                <Text size="sm">No terms are available.</Text>
              )}
            </Box>

            <Checkbox
              color={accent}
              mt="xl"
              checked={termsAccepted}
              onChange={e => setTermsAccepted(e.currentTarget.checked)}
              label="I understand the terms above."
            />

            <Group justify="space-between" mt="xl">
              <Button variant="default" onClick={prevStep}>
                Back
              </Button>
              <Button
                color={accent}
                onClick={nextStep}
                disabled={!termsAccepted}
              >
                Next
              </Button>
            </Group>
          </Stepper.Step>

          {/* ── Step 5: Sign & submit ──────────────────────────────────── */}
          <Stepper.Step label="Sign" description="Initial & submit">
            <Box mt="md" ta="center">
              <Title order={3} mb="xs">
                Thank you!
              </Title>
              <Text c="dimmed">
                Thanks for verifying your information! We're excited to get
                started — please add your initials below to confirm that
                everything is correct and we will see you soon!
              </Text>
            </Box>

            <Text size="sm" fw={500} mt="xl" mb="xs">
              Your initials
            </Text>
            <Box className="signature-box">
              <SignatureCanvas
                ref={sigCanvasRef}
                penColor="#000000"
                backgroundColor="#ffffff"
                canvasProps={{
                  className: "signature-canvas-inline",
                  width: 460,
                  height: 150,
                }}
              />
            </Box>
            <Group justify="flex-end" mt="xs">
              <Button
                variant="subtle"
                size="xs"
                onClick={clearSignature}
                disabled={submitting}
              >
                Clear
              </Button>
            </Group>

            {error && (
              <Alert color="red" mt="lg" title="Error">
                {error}
              </Alert>
            )}

            <Group justify="space-between" mt="xl">
              <Button
                variant="default"
                onClick={prevStep}
                disabled={submitting}
              >
                Back
              </Button>
              <Button
                color={accent}
                onClick={handleSubmit}
                loading={submitting}
              >
                Submit
              </Button>
            </Group>
          </Stepper.Step>
        </Stepper>
      </Paper>
    </div>
  );
};

export default InfoVerificationPage;
